import {RecentAccountActivityDto} from '../dtos/recentAccountActivity.dto';
import {
    DeviceTypes,
    SpecialityTypes,
    SubscriptionPeriodUnit,
    SubscriptionPeriodUnitUtils,
    SubscriptionSource,
    SubscriptionSourceUtils,
    TextUtils
} from 'common';

export class RecentAccountActivityReportDataViewModel {
    code: string;
    position: number;
    fullName: string;
    email: string;
    phone: string;
    specialty: string;
    createdOnDate: number;
    country: string;
    zipCode: string;
    accountRole: string;
    subscription: string;
    subscriptionPeriodUnit: string;
    subscriptionAuthor: string;
    serialCodes: string;
    nextPaymentDate: number;
    lastPaymentDate: number;
    expirationDate: number;
    sessionsOneWeek: number;
    sessionsOneMonth: number;
    sessionsThreeMonths: number;
    sessionsSixMonths: number;
    sessionsYear: number;
    totalSessions: number;
    lastUsedDate: number;
    subscriptionSource: string;
    totalNumberOfPayments: number;
    totalPaymentsAmount: number;
    notes: string;

    constructor() {
        // creating a field for each device
        DeviceTypes.all().forEach(deviceType => {
                const key = deviceType.toLowerCase();
                this[key] = 0;
                this[RecentAccountActivityReportDataViewModel.totalSessionsKey(key)] = 0;
            }
        );
    }

    public static totalSessionsKey(key: string): string {
        return key + '_total';
    }

    public static from(reports: RecentAccountActivityDto[]): RecentAccountActivityReportDataViewModel[] {
        const data = [];
        reports.forEach((report, i) => {
            const convertedData = new RecentAccountActivityReportDataViewModel();
            convertedData.code = report.code;
            convertedData.position = i + 1;
            convertedData.fullName = report.fullName;
            convertedData.email = report.email;
            convertedData.phone = report.telephone;
            convertedData.accountRole = report.accountRole;
            convertedData.specialty = '';
            if (!TextUtils.isEmpty(report.specialty)) {
                // Specialty is a string field that has lots of values with comma separation.
                // Comma is used in CSVs so we need to append : here.
                report.specialty.split(',').forEach(specialty => {
                    convertedData.specialty += SpecialityTypes.format(SpecialityTypes.parse(specialty)) + ':';
                });

                convertedData.specialty = convertedData.specialty.slice(0, -1);
            } else {
                convertedData.specialty = '-';
            }

            convertedData.createdOnDate = report.createdOn;
            convertedData.country = report.country;
            convertedData.zipCode = report.zipCode;
            convertedData.serialCodes = '';
            if (report.serialCodes.length >= 0) {
                report.serialCodes.forEach(serialCode => {
                    convertedData.serialCodes += serialCode + ':';
                });
                // Remove last : char. The : char is done in order to group in CSV
                convertedData.serialCodes = convertedData.serialCodes.slice(0, -1);
            } else {
                convertedData.serialCodes = '-';
            }

            convertedData.totalNumberOfPayments = report.totalNumberOfPayments;
            convertedData.totalPaymentsAmount = report.totalPaymentsAmount;

            convertedData.subscriptionAuthor = report.latestNonExpiredSubscription?.subscriptionAuthor;
            convertedData.subscription = report.latestNonExpiredSubscription?.subscription;
            convertedData.subscriptionSource = SubscriptionSourceUtils.title(report.latestNonExpiredSubscription?.subscriptionSource as SubscriptionSource);
            convertedData.subscriptionPeriodUnit = this.getSubscriptionPeriodUnitEmptyName(
                report.latestNonExpiredSubscription?.subscriptionPeriodUnit, convertedData.subscriptionAuthor);
            convertedData.notes = report.latestNonExpiredSubscription?.notes;
            convertedData.nextPaymentDate = report.latestNonExpiredSubscription?.nextPayment;
            convertedData.lastPaymentDate = report.latestNonExpiredSubscription?.lastPayment;
            convertedData.subscriptionAuthor = report.latestNonExpiredSubscription?.subscriptionAuthor;
            convertedData.expirationDate = report.latestNonExpiredSubscription?.expirationDate;

            convertedData.lastUsedDate = report.lastUsed;
            if (report.deviceSessions) {
                report.deviceSessions.forEach(deviceSession => {
                    const key = deviceSession.device.toLowerCase();
                    convertedData[key] = deviceSession.sessionsInThreeMonths ?? 0;
                    convertedData[RecentAccountActivityReportDataViewModel.totalSessionsKey(key)] = deviceSession.totalSessions ?? 0;
                });
            }
            convertedData.sessionsOneWeek = report.sessionsOneWeek ?? 0;
            convertedData.sessionsOneMonth = report.sessionsOneMonth ?? 0;
            convertedData.sessionsThreeMonths = report.sessionsThreeMonths ?? 0;
            convertedData.sessionsSixMonths = report.sessionsSixMonths ?? 0;
            convertedData.sessionsYear = report.sessionsTwelveMonths ?? 0;
            convertedData.totalSessions = report.totalSessions;

            data.push(convertedData);
        });
        return data;
    }

    private static getSubscriptionPeriodUnitEmptyName(subscriptionPeriodUnit?: string, author?: string): string {
        let name = '';
        if (!TextUtils.isEmpty(subscriptionPeriodUnit)) {
            name = subscriptionPeriodUnit;
        } else if (!TextUtils.isEmpty(author)) {
            name = SubscriptionPeriodUnitUtils.title(SubscriptionPeriodUnit.CUSTOM);
        }
        return name;
    }
}
